import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { courseAPI, categoryAPI, tagAPI, BASE_URL } from '../services/api';
import Select from 'react-select';

function CreateCourse() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [error, setError] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    code: '',
    tags: [],
    category: '',
    status: 'draft',
    startDate: '',
    endDate: '',
    thumbnail: '',
    thumbnailPublicId: '',
    settings: {
      allowSelfEnrollment: false,
      requireApproval: false,
      maxStudents: '',
      showProgressToStudents: true,
      allowDiscussion: false
    }
  });

  useEffect(() => {
    if (user && user.role !== 'admin') {
      setError('Access denied. Admin only.');
      navigate('/user');
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchCategories();
    fetchTags();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await categoryAPI.getAllCategories();
      setCategories(response.data.categories || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await tagAPI.getAllTags();
      setTags(response.data.tags || []);
    } catch (err) {
      console.error('Error fetching tags:', err);
    }
  };

  const handleCreateCourse = async (e) => {
    e.preventDefault();
    
    try {
      const courseData = {
        ...formData,
        settings: {
          ...formData.settings,
          maxStudents: formData.settings.maxStudents ? parseInt(formData.settings.maxStudents) : null
        }
      };
      
      const response = await courseAPI.createCourse(courseData);
      const newCourse = response.data.course;
      
      // Navigate to the course builder for the newly created course
      navigate(`/course-builder/${newCourse._id}`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create course');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'courses');

    try {
      setUploadingImage(true);
      const response = await fetch(`${BASE_URL}/files/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (!response.ok) throw new Error('Upload failed');

      const data = await response.json();
      setFormData(prev => ({
        ...prev,
        thumbnail: data.url,
        thumbnailPublicId: data.public_id
      }));
    } catch (err) {
      alert('Failed to upload image');
      console.error('Error uploading image:', err);
    } finally {
      setUploadingImage(false);
    }
  };

  return (
    <div className="container" style={{ paddingTop: '20px', maxWidth: '900px', margin: '0 auto' }}>
      <div className="card">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '24px',
          paddingBottom: '16px',
          borderBottom: '2px solid #e2e8f0'
        }}>
          <div>
            <h1 style={{ margin: '0 0 8px 0', fontSize: '28px', color: '#1a202c' }}>
              Create New Course
            </h1>
            <p style={{ margin: 0, color: '#718096', fontSize: '15px' }}>
              Set up your course basics and get started
            </p>
          </div>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/courses')}
            style={{ fontSize: '14px' }}
          >
            ← Back to Courses
          </button>
        </div>

        {error && (
          <div className="error" style={{ marginBottom: '20px' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleCreateCourse}>
          <div style={{ display: 'grid', gap: '20px' }}>
            {/* Basic Information Section */}
            <div style={{
              padding: '20px',
              background: '#f7fafc',
              borderRadius: '8px',
              border: '1px solid #e2e8f0'
            }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#1a202c' }}>
                📚 Basic Information
              </h3>

              <div className="form-group">
                <label style={{ color: '#1a202c', fontWeight: '600' }}>
                  Course Title *
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Introduction to React"
                  style={{ 
                    background: '#ffffff', 
                    color: '#1a202c', 
                    border: '1px solid #e2e8f0',
                    fontSize: '15px'
                  }}
                />
              </div>

              <div className="form-group">
                <label style={{ color: '#1a202c', fontWeight: '600' }}>
                  Course Code *
                </label>
                <input
                  type="text"
                  required
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="e.g., CS101"
                  style={{ 
                    background: '#ffffff', 
                    color: '#1a202c', 
                    border: '1px solid #e2e8f0', 
                    textTransform: 'uppercase',
                    fontSize: '15px'
                  }}
                />
                <p style={{ fontSize: '13px', color: '#718096', marginTop: '6px' }}>
                  A unique identifier for this course
                </p>
              </div>

              <div className="form-group">
                <label style={{ color: '#1a202c', fontWeight: '600' }}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Provide a brief overview of what students will learn..."
                  rows="4"
                  style={{ 
                    background: '#ffffff', 
                    color: '#1a202c', 
                    border: '1px solid #e2e8f0',
                    fontSize: '15px'
                  }}
                />
              </div>
            </div>

            {/* Visual & Classification Section */}
            <div style={{
              padding: '20px',
              background: '#f7fafc',
              borderRadius: '8px',
              border: '1px solid #e2e8f0'
            }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#1a202c' }}>
                🎨 Visual & Classification
              </h3>

              <div className="form-group">
                <label style={{ color: '#1a202c', fontWeight: '600' }}>
                  Course Thumbnail
                </label>
                {formData.thumbnail ? (
                  <div style={{ 
                    background: '#ffffff',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '2px solid #48bb78'
                  }}>
                    <div style={{ 
                      marginBottom: '12px',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#48bb78'
                    }}>
                      ✓ Thumbnail Preview
                    </div>
                    <div style={{ 
                      position: 'relative',
                      width: '100%',
                      maxWidth: '600px'
                    }}>
                      <img 
                        src={formData.thumbnail} 
                        alt="Course thumbnail" 
                        style={{ 
                          width: '100%',
                          height: 'auto',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0'
                        }} 
                      />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, thumbnail: '', thumbnailPublicId: '' })}
                        className="btn btn-danger"
                        style={{
                          marginTop: '12px',
                          width: '100%'
                        }}
                      >
                        🗑️ Remove Thumbnail
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploadingImage}
                      style={{ 
                        width: '100%', 
                        padding: '10px', 
                        border: '1px solid #e2e8f0', 
                        borderRadius: '6px',
                        background: '#ffffff',
                        color: '#1a202c'
                      }}
                    />
                    {uploadingImage && (
                      <p style={{ fontSize: '13px', color: '#667eea', marginTop: '8px' }}>
                        🔄 Uploading image...
                      </p>
                    )}
                    <p style={{ fontSize: '13px', color: '#718096', marginTop: '6px' }}>
                      Recommended size: 600 x 400 pixels. Max file size: 5MB
                    </p>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label style={{ color: '#1a202c', fontWeight: '600' }}>
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  style={{ 
                    background: '#ffffff', 
                    color: '#1a202c', 
                    border: '1px solid #e2e8f0',
                    fontSize: '15px'
                  }}
                >
                  <option value="">Select Category</option>
                  {categories.map(cat => (
                    <option key={cat._id} value={cat._id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label style={{ color: '#1a202c', fontWeight: '600' }}>
                  Tags
                </label>
                <Select
                  isMulti
                  menuPortalTarget={document.body}
                  options={tags.map(tag => ({ value: tag._id, label: tag.name }))}
                  value={formData.tags.map(tagId => {
                    const tag = tags.find(t => t._id === tagId);
                    return tag ? { value: tag._id, label: tag.name } : null;
                  }).filter(Boolean)}
                  onChange={(selected) => setFormData({ ...formData, tags: selected ? selected.map(s => s.value) : [] })}
                  styles={{
                    control: (base) => ({ 
                      ...base, 
                      background: '#ffffff', 
                      borderColor: '#e2e8f0', 
                      color: '#1a202c',
                      fontSize: '15px'
                    }),
                    menu: (base) => ({ ...base, background: '#ffffff' }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    option: (base, state) => ({ 
                      ...base, 
                      background: state.isFocused ? '#f7fafc' : '#ffffff', 
                      color: '#1a202c' 
                    }),
                    multiValue: (base) => ({ ...base, background: '#edf2f7' }),
                    multiValueLabel: (base) => ({ ...base, color: '#1a202c' }),
                    input: (base) => ({ ...base, color: '#1a202c' })
                  }}
                />
                <p style={{ fontSize: '13px', color: '#718096', marginTop: '6px' }}>
                  Add relevant tags to help students find your course
                </p>
              </div>
            </div>

            {/* Schedule & Status Section */}
            <div style={{
              padding: '20px',
              background: '#f7fafc',
              borderRadius: '8px',
              border: '1px solid #e2e8f0'
            }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#1a202c' }}>
                📅 Schedule & Status
              </h3>

              <div className="form-group">
                <label style={{ color: '#1a202c', fontWeight: '600' }}>
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  style={{ 
                    background: '#ffffff', 
                    color: '#1a202c', 
                    border: '1px solid #e2e8f0',
                    fontSize: '15px'
                  }}
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
                <p style={{ fontSize: '13px', color: '#718096', marginTop: '6px' }}>
                  Draft courses are only visible to admins
                </p>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label style={{ color: '#1a202c', fontWeight: '600' }}>
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    style={{ 
                      background: '#ffffff', 
                      color: '#1a202c', 
                      border: '1px solid #e2e8f0',
                      fontSize: '15px'
                    }}
                  />
                </div>

                <div className="form-group">
                  <label style={{ color: '#1a202c', fontWeight: '600' }}>
                    End Date
                  </label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    style={{ 
                      background: '#ffffff', 
                      color: '#1a202c', 
                      border: '1px solid #e2e8f0',
                      fontSize: '15px'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              justifyContent: 'flex-end',
              paddingTop: '8px'
            }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate('/courses')}
                style={{ fontSize: '15px', padding: '12px 24px' }}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn btn-primary"
                style={{ fontSize: '15px', padding: '12px 32px' }}
              >
                Create Course →
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateCourse;
